import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  withLatestFrom,
} from 'rxjs/operators';
import { AccountService } from '@core/services';
import {
  ACCOUNT_CREATE_VIEW,
  ACCOUNT_UPDATE_VIEW,
  ACCOUNT_FIND,
  ACCOUNT_FIND_ALL,
  ACCOUNT_CREATE,
  ACCOUNT_UPDATE,
  ACCOUNT_REMOVE,
  ACCOUNT_ACTIVATE,
  ACCOUNT_DEACTIVATE,
  ACCOUNT_EDIT_MODE,
  ACCOUNT_UPDATE_ID,
  ACCOUNT_FIND_SUCCESS,
  ACCOUNT_FIND_ALL_SUCCESS,
  ACCOUNT_UPDATE_LOADING,
  ACCOUNT_CREATE_COMPLETED,
  ACCOUNT_UPDATE_COMPLETED,
  ACCOUNT_REMOVE_COMPLETED,
  ACCOUNT_RESET,
  ACCOUNT_RESET_FORM,
  ACCOUNT_RESET_ERROR,
  SERVICE_FAILURE,
  NOTIFICATION_SHOW,
  ACCOUNT_ACTIVATE_COMPLETED,
  ACCOUNT_DEACTIVATE_COMPLETED,
} from '@store/actions';
import {
  selectAccountId,
  selectAccountForm,
} from '@store/selectors';
import { Cuenta, CuentaForm } from '@core/models';

@Injectable()
export class AccountEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private accountService: AccountService,
  ) {}

  createForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ACCOUNT_CREATE_VIEW),
      mergeMap(() => {
        return [
          ACCOUNT_RESET(),
          ACCOUNT_EDIT_MODE({ editMode: true, isEditing: false }),
        ];
      }),
    )
  );

  editForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ACCOUNT_UPDATE_VIEW),
      mergeMap(({ accountId }) => {
        return [
          ACCOUNT_UPDATE_ID({ accountId }),
          ACCOUNT_EDIT_MODE({ editMode: true, isEditing: true }),
        ];
      }),
    )
  );

  find$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ACCOUNT_FIND),
      filter((action) => action.id !== null),
      mergeMap(({ id }) => {
        this.store.dispatch(ACCOUNT_UPDATE_LOADING({ loading: true }));

        return this.accountService.find(id).pipe(
          mergeMap(({ data }) => {
            const [account] = data as Cuenta[];

            return [
              ACCOUNT_UPDATE_LOADING({ loading: false }),
              ACCOUNT_FIND_SUCCESS({ account }),
            ];
          }),
          catchError((error) =>
            of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  ACCOUNT_UPDATE_LOADING({ loading: false }),
                ];
              })
            )
          )
        );
      }),
    )
  );

  findAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ACCOUNT_FIND_ALL),
      mergeMap(({ resellerId, name }) => {
        this.store.dispatch(ACCOUNT_UPDATE_LOADING({ loading: true }));

        return this.accountService.findAll({ resellerId, name }).pipe(
          mergeMap(({ data }) => {
            const accountList = data as Cuenta[];

            return [
              ACCOUNT_FIND_ALL_SUCCESS({ accountList }),
              ACCOUNT_UPDATE_LOADING({ loading: false }),
            ];
          }),
          catchError((error) =>
            of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  ACCOUNT_UPDATE_LOADING({ loading: false }),
                ];
              })
            )
          )
        );
      }),
    )
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ACCOUNT_CREATE),
      withLatestFrom(
        this.store.select(selectAccountForm),
      ),
      filter(([_, storeForm]) => !!storeForm),
      map(([_, storeForm]) => ({ storeForm: storeForm as CuentaForm })),
      mergeMap(({ storeForm }) => {
        this.store.dispatch(ACCOUNT_UPDATE_LOADING({ loading: true }));
        return this.accountService.create(storeForm).pipe(
          mergeMap(() => {
            return [
              NOTIFICATION_SHOW({ notification: { type: 'success', title: 'Success', detail: 'Account successfully created' } }),
              ACCOUNT_UPDATE_LOADING({ loading: false }),
              ACCOUNT_CREATE_COMPLETED(),
            ];
          }),
          catchError((error) => {
            return of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  ACCOUNT_RESET_ERROR(),
                  ACCOUNT_UPDATE_LOADING({ loading: false }),
                ];
              })
            )
          })
        );
      }),
    )
  );

  creationComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ACCOUNT_CREATE_COMPLETED),
      mergeMap(() => {
        this.accountService.createCompleted();
        
        return [
          ACCOUNT_RESET(),
          ACCOUNT_RESET_FORM(),
          ACCOUNT_EDIT_MODE({ editMode: false, isEditing: false }),
        ]
      })
    ),
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ACCOUNT_UPDATE),
      withLatestFrom(
        this.store.select(selectAccountId),
        this.store.select(selectAccountForm),
      ),
      filter(([_, accountId, accountForm]) => !!accountId && !!accountForm),
      map(([_, accountId, accountForm]) => ({ accountForm: accountForm as CuentaForm, accountId })),
      mergeMap(({ accountForm, accountId }) => {
        this.store.dispatch(ACCOUNT_UPDATE_LOADING({ loading: true }));
        return this.accountService.update(accountId!, accountForm).pipe(
          mergeMap(() => {
            return [
              NOTIFICATION_SHOW({ notification: { type: 'success', title: 'Success', detail: 'Account successfully updated' } }),
              ACCOUNT_UPDATE_LOADING({ loading: false }),
              ACCOUNT_UPDATE_COMPLETED(),
            ];
          }),
          catchError((error) => {
            return of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  ACCOUNT_RESET_ERROR(),
                  ACCOUNT_UPDATE_LOADING({ loading: false }),
                ];
              })
            )
          })
        );
      }),
    )
  );

  updateComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ACCOUNT_UPDATE_COMPLETED),
      mergeMap(() => {
        this.accountService.updateCompleted();
        
        return [
          ACCOUNT_RESET(),
          ACCOUNT_RESET_FORM(),
          ACCOUNT_UPDATE_ID({ accountId: null }),
          ACCOUNT_EDIT_MODE({ editMode: false, isEditing: false }),
        ]
      }),
    ),
  );

  activate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ACCOUNT_ACTIVATE),
      filter(({ id }) => !!id),
      mergeMap(({ id }) => {
        this.store.dispatch(ACCOUNT_UPDATE_LOADING({ loading: true }));

        return this.accountService.activate(id).pipe(
          mergeMap(() => {
            return [
              NOTIFICATION_SHOW({ notification: { type: 'success', title: 'Success', detail: 'Account status updated successfully' } }),
              ACCOUNT_UPDATE_LOADING({ loading: false }),
              ACCOUNT_ACTIVATE_COMPLETED(),
            ];
          }),
          catchError((error) => {
            return of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  ACCOUNT_RESET_ERROR(),
                  ACCOUNT_UPDATE_LOADING({ loading: false }),
                ];
              })
            )
          })
        );
      }),
    )
  );

  activateComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ACCOUNT_ACTIVATE_COMPLETED),
      mergeMap(() => {
        this.accountService.closeDialog();
        return [
          ACCOUNT_FIND_ALL({}),
        ];
      })
    ),
  );

  deactivate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ACCOUNT_DEACTIVATE),
      filter(({ id, razonInactivacion }) => !!id && !!razonInactivacion?.trim()),
      mergeMap(({ id, razonInactivacion }) => {
        this.store.dispatch(ACCOUNT_UPDATE_LOADING({ loading: true }));

        return this.accountService.deactivate(id, razonInactivacion).pipe(
          mergeMap(() => {
            return [
              NOTIFICATION_SHOW({ notification: { type: 'success', title: 'Success', detail: 'Account status updated successfully' } }),
              ACCOUNT_UPDATE_LOADING({ loading: false }),
              ACCOUNT_DEACTIVATE_COMPLETED(),
            ];
          }),
          catchError((error) => {
            return of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  ACCOUNT_RESET_ERROR(),
                  ACCOUNT_UPDATE_LOADING({ loading: false }),
                ];
              })
            )
          })
        );
      }),
    )
  );

  deactivateComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ACCOUNT_DEACTIVATE_COMPLETED),
      mergeMap(() => {
        this.accountService.closeDialog();
        return [
          ACCOUNT_FIND_ALL({}),
        ];
      })
    ),
  );

  remove$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ACCOUNT_REMOVE),
      filter(({ id }) => !!id),
      mergeMap(({ id }) => {

        return this.accountService.remove(id).pipe(
          mergeMap(() => {
            return [
              NOTIFICATION_SHOW({ notification: { type: 'success', title: 'Success', detail: 'Account removed successfully' } }),
              ACCOUNT_REMOVE_COMPLETED(),
            ];
          }),
          catchError((error) => {
            return of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  ACCOUNT_RESET_ERROR(),
                ];
              })
            )
          })
        );
      }),
    )
  );

  removeComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ACCOUNT_REMOVE_COMPLETED),
      mergeMap(() => {
        return [
          ACCOUNT_FIND_ALL({}),
        ]
      })
    ),
  );
}
